import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $declinedAuthStore } from '@lib/store';
import { cn } from '@shared/utils';
import { AlertTypeEnum, type RecipientDetail } from '@shared/api';
import { AddRecipientModal } from './addRecipientModal';
import { Button, Icon, Modal, Text } from '@shared/widgets';
import CrossIcon from '@assets/icons/cross.svg';

interface EditRecipientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId?: number;
  recipients?: RecipientDetail[];
  subId?: string;
  cardId?: number;
}

export const EditRecipientsModal = (props: EditRecipientsModalProps) => {
  const declinedAuthStore = useUnit($declinedAuthStore.store);
  const recipients = declinedAuthStore.recipient;

  const [showAddRecipientModal, setShowAddRecipientModal] = useState<boolean>(false);

  const { t } = useTranslation('account');

  useEffect(() => {
    if (props.isOpen) $declinedAuthStore.events.getRecipient(props.recipients || []);
  }, [props.isOpen]);

  useEffect(() => {
    return () => $declinedAuthStore.events.clear();
  }, []);

  const save = async () => {
    if (!props.accountId && !props.cardId) return;
    if (props.subId) {
      if (recipients.length === 0)
        await $declinedAuthStore.events.deleteDeclinedAuth({ subscriptionId: props.subId, cardId: props.cardId, accountId: props.accountId });
      else await $declinedAuthStore.events.updateDeclinedAuth({ subscriptionId: props.subId, request: { recipients, override: true } });
    } else {
      if (!props.cardId) await $declinedAuthStore.events.addDeclinedAuth({ recipients, accountId: props.accountId });
      else await $declinedAuthStore.events.addDeclinedAuth({ recipients, cardId: props.cardId });
    }
    props.onClose();
  };

  return (
    <>
      <Modal isOpen={props.isOpen} onClose={props.onClose} loading={declinedAuthStore.loading} title={t('sidebar.modals.editRecipients.title')}>
        <Text className='mb-3 ml-1' size='sm'>
          {t('sidebar.modals.editRecipients.description')}
        </Text>
        <div className='flex flex-col gap-2'>
          {recipients.map((recipient, idx) => (
            <div key={idx} className='flex w-full items-center justify-between rounded-sm bg-white p-2 pl-3 dark:bg-slate'>
              <div className='shrink pr-4'>
                <Text className='text-ellipsis' size='sm'>
                  {recipient.firstName}
                </Text>
                <Text className='text-ellipsis' size='xs' color='gray'>
                  {recipient.email}
                </Text>
              </div>
              <Button className='p-0' color='icon' onClick={() => $declinedAuthStore.events.removeRecipient(recipient.email!)}>
                <Icon icon={<CrossIcon className='h-6 w-6' />} color='red' />
              </Button>
            </div>
          ))}
        </div>
        <a
          className={cn('mt-2 block w-fit cursor-pointer text-gothic underline', 'hover:no-underline')}
          onClick={() => setShowAddRecipientModal(true)}
        >
          {t('sidebar.modals.editRecipients.buttons.addNewRecipient')}
        </a>
        <Button className='mt-7' fullWidth color='primary' disabled={props.subId === undefined && recipients.length === 0} onClick={save}>
          {t('buttons.confirm', { ns: 'common' })}
        </Button>
      </Modal>
      <AddRecipientModal show={showAddRecipientModal} onClose={() => setShowAddRecipientModal(false)} alertType={AlertTypeEnum.declinedAuth} />
    </>
  );
};
